import { sectionRenderer } from '@theme/section-renderer';

/**
 * PILGRIM search rails — fills the <pilgrim-search-rails> band on the search
 * results page (sections/pilgrim-search-rails.liquid) with the Kollektioner +
 * Indhold groups the drawer shows for the same query.
 *
 * - One fetch per page load, through the drawer's endpoint section
 *   (pilgrim-search-drawer-results) via the Predictive Search API —
 *   collections can never come from /search, and reusing the endpoint keeps
 *   markup, translations and caps identical to the drawer. Only
 *   collection/page/article resources are requested (the page's grid already
 *   owns products).
 * - The band removes itself when nothing matches, when the query is too
 *   short, or on any error — rails are an enhancement and must never break
 *   the results page.
 * - CLS guard: the rendered band height is stored per query in
 *   sessionStorage and reserved as min-height on repeat loads of the same
 *   query (back/forward, pagination). The first load per query still grows
 *   in; measured against the CLS=0 baseline in QA.
 * - Pushes the guarded pilgrim_search dataLayer event on rail clicks
 *   (resource group + position), same pattern as the drawer.
 * - Owns the results page's one-way "Vis flere" button (mobile only —
 *   desktop clips to one row): a document-delegated click handler, so the
 *   zero-results band rendered by pilgrim-search-results (outside this
 *   element) is covered by the same code.
 */
class PilgrimSearchRails extends HTMLElement {
  static MIN_CHARS = 2;
  static STORAGE_PREFIX = 'pilgrim:rails-height:';

  #query = '';

  connectedCallback() {
    this.#query = (this.dataset.query ?? '').trim();
    const endpointSectionId = this.dataset.endpointSectionId;

    if (this.#query.length < PilgrimSearchRails.MIN_CHARS || !endpointSectionId) {
      this.remove();
      return;
    }

    const storedHeight = this.#readStoredHeight();
    if (storedHeight === 0) {
      // This query had no rail matches earlier this session — skip the fetch.
      this.remove();
      return;
    }
    if (storedHeight) {
      // Capped: a stored height from another viewport (or a degenerate
      // rendering context) must never reserve more than half a screen.
      this.style.minHeight = `min(${storedHeight}px, 50vh)`;
    }

    this.#load(endpointSectionId);
  }

  /**
   * @param {string} endpointSectionId
   */
  async #load(endpointSectionId) {
    try {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', this.#query);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[type]', 'collection,page,article');

      const markup = await sectionRenderer.getSectionHTML(endpointSectionId, false, url);
      if (!markup) {
        this.#dismiss();
        return;
      }

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const groups = ['collections', 'content']
        .map((group) => parsed.querySelector(`[data-pilgrim-search-group="${group}"]`))
        .filter((group) => group !== null);

      if (groups.length === 0) {
        this.#dismiss();
        return;
      }

      this.replaceChildren(...groups);
      this.style.minHeight = '';
      // Never record a height measured in a degenerate rendering context
      // (hidden preview panes report a 0-wide viewport and reflow the band
      // to nonsense — it would over-reserve on the next real load).
      if (window.innerWidth > 0) {
        this.#storeHeight(this.offsetHeight);
      }
      this.addEventListener('click', this.#handleClick);
    } catch {
      this.remove();
    }
  }

  #dismiss() {
    this.#storeHeight(0);
    this.remove();
  }

  /* ------------------------------------------------------------------ */
  /* CLS reservation (per-query, per-session)                            */
  /* ------------------------------------------------------------------ */

  get #storageKey() {
    return `${PilgrimSearchRails.STORAGE_PREFIX}${this.#query.toLowerCase()}`;
  }

  /**
   * @returns {number | null}
   */
  #readStoredHeight() {
    try {
      const stored = sessionStorage.getItem(this.#storageKey);
      if (stored === null) return null;
      const height = Number.parseInt(stored, 10);
      return Number.isFinite(height) && height >= 0 ? height : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {number} height
   */
  #storeHeight(height) {
    try {
      sessionStorage.setItem(this.#storageKey, String(Math.round(height)));
    } catch {
      // Storage unavailable (private mode) — the reservation just stays off.
    }
  }

  /* ------------------------------------------------------------------ */
  /* Analytics                                                           */
  /* ------------------------------------------------------------------ */

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const anchor = target?.closest('a');
    if (!anchor) return;

    const group = anchor.closest('[data-pilgrim-search-group]');
    if (!(group instanceof HTMLElement)) return;

    const position = [...group.querySelectorAll('a')].indexOf(anchor) + 1;

    try {
      window.dataLayer?.push({
        event: 'pilgrim_search',
        pilgrim_search_action: 'rails_click',
        pilgrim_search_resource: group.dataset.pilgrimSearchGroup,
        pilgrim_search_position: position,
        search_term: this.#query,
      });
    } catch {
      // Analytics must never break navigation.
    }
  };
}

/**
 * Vis flere on the results page — rails band AND the zero-results band.
 * One-way (Hjalde 2026-09-02): reveal the group's --overflow rows, drop the
 * button. Delegated at the document because the zero band lives outside
 * <pilgrim-search-rails>; the drawer's buttons sit inside
 * .pilgrim-search-drawer, never inside .pilgrim-search-band, so the two
 * handlers never overlap.
 */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('.pilgrim-search-band [data-pilgrim-search-more]');
  if (!(button instanceof HTMLButtonElement)) return;

  const group = button.closest('[data-pilgrim-search-group]');
  if (!group) return;

  group.classList.add('pilgrim-search-drawer__group--expanded');
  // Hidden, not removed — same contract as the drawer (there a detached
  // click target reads as an outside click and closes the dialog).
  button.hidden = true;
});

/**
 * Recent searches block on the search page (blocks/_pilgrim-search-recent):
 * filled from the same localStorage list the drawer writes, as links that
 * submit the term the way the drawer does (type=product + prefix contract).
 * Stays hidden without entries; Clear empties the shared list.
 */
const RECENT_KEY = 'pilgrim:recent-searches';

function readRecentSearches() {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string' && term.trim()) : [];
  } catch {
    return [];
  }
}

function renderRecentSearches() {
  const searchUrl = window.Theme?.routes?.search_url ?? '/search';
  for (const group of document.querySelectorAll('[data-pilgrim-search-recent]')) {
    const list = group.querySelector('[data-pilgrim-search-recent-list]');
    if (!list) continue;
    const terms = readRecentSearches();
    list.textContent = '';
    if (terms.length === 0) {
      if (!window.Shopify?.designMode) group.hidden = true;
      continue;
    }
    for (const term of terms) {
      const item = document.createElement('li');
      const link = document.createElement('a');
      const url = new URL(searchUrl, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      link.href = url.toString();
      link.className = 'pilgrim-search-drawer__recent-term';
      link.textContent = term;
      item.append(link);
      list.append(item);
    }
    group.hidden = false;
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target?.closest('[data-pilgrim-search-recent-clear]')) return;
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
  renderRecentSearches();
});

renderRecentSearches();

if (!customElements.get('pilgrim-search-rails')) {
  customElements.define('pilgrim-search-rails', PilgrimSearchRails);
}
